import { useState } from "react";
import { BookmarkPlus } from "lucide-react";
import { Permission } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { useHasPermission } from "@/store/authStore";
import { SaveAsTemplateSheet } from "./SaveAsTemplateSheet";

/** Tooltip shown while autosave has not yet persisted the latest edits. */
const PENDING_SAVE_HINT =
  "Available once your changes finish saving - templates capture the saved document.";

interface SaveAsTemplateButtonProps {
  /** The quote whose stored document is captured as the template. */
  quoteId: string;
  /** True while there are unsaved edits pending a save. */
  isDirty: boolean;
  /** True while a save request is in flight. */
  isSaving: boolean;
}

/**
 * Editor-header action opening the save-as-template sheet. The server captures
 * the quote's stored document, so the action waits for autosave to settle.
 * @param quoteId - The quote whose stored document is captured
 * @param isDirty - True while there are unsaved edits pending a save
 * @param isSaving - True while a save request is in flight
 * @returns The save-as-template button element, or null without permission
 */
export function SaveAsTemplateButton({
  quoteId,
  isDirty,
  isSaving,
}: SaveAsTemplateButtonProps) {
  const canEditSettings = useHasPermission(Permission.SETTINGS_EDIT);
  const [open, setOpen] = useState(false);

  if (!canEditSettings) {
    return null;
  }

  const pending = isDirty || isSaving;

  return (
    <>
      <span title={pending ? PENDING_SAVE_HINT : undefined}>
        <Button
          variant="ghost"
          size="sm"
          disabled={pending}
          onClick={() => setOpen(true)}
        >
          <BookmarkPlus className="size-4" /> Save as template
        </Button>
      </span>
      <SaveAsTemplateSheet
        open={open}
        onOpenChange={setOpen}
        quoteId={quoteId}
      />
    </>
  );
}
